<script setup lang="ts">
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  LinearScale
} from 'chart.js'
import { 
  VennDiagramController, 
  EulerDiagramController, 
  ArcSlice 
} from 'chartjs-chart-venn'
import type { AvailabilityResult } from '@/types/validation'
import chroma from 'chroma-js'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { theme } from '#tailwind-config'

// Register Chart.js components and the Euler diagram controllers
ChartJS.register(
  Tooltip,
  Legend,
  LinearScale,
  VennDiagramController,
  EulerDiagramController,
  ArcSlice
)

const props = defineProps<{
  data: Record<string, AvailabilityResult>
}>()

const colorMode = useColorMode()
const chartRef = ref<ChartJS | null>(null)
const canvasRef = ref<HTMLCanvasElement | null>(null)

// Toggle between species and suppliers mode
const showSuppliers = ref(false)

// Use the same color palette as TreeMap for consistency
const tailwindPastelColors = [
  'rgb(253, 186, 116)', // orange-300
  'rgb(147, 197, 253)', // blue-300
  'rgb(134, 239, 172)', // green-300
  'rgb(252, 165, 165)', // red-300
  'rgb(216, 180, 254)', // purple-300
  'rgb(253, 224, 71)',  // yellow-300
  'rgb(249, 168, 212)', // pink-300
  'rgb(125, 211, 252)', // sky-300
  'rgb(153, 246, 228)', // teal-300
  'rgb(196, 181, 253)', // indigo-300
]

// Generate a color for each item (species or supplier)
const getItemColor = (index: number) => {
  return tailwindPastelColors[index % tailwindPastelColors.length]
}

// Helper function to wrap text (single line with longer length)
const wrapText = (text: string, maxLength: number = 25) => {
  if (text.length <= maxLength) return text
  
  // Find a good breaking point (space) close to maxLength
  const words = text.split(' ')
  let result = ''
  
  for (const word of words) {
    if ((result + word).length <= maxLength) {
      result += (result ? ' ' : '') + word
    } else {
      break
    }
  }
  
  // If we couldn't fit any words, just truncate
  return result || text.substring(0, maxLength - 3) + '...'
}

// Helper function to blend colors for overlapping areas
const blendColors = (colorIndices: number[]) => {
  if (colorIndices.length === 1) {
    return getItemColor(colorIndices[0])
  }
  
  // Get the colors for the overlapping items
  const colors = colorIndices.map(index => getItemColor(index))
  
  // Use chroma.js to mix/average the colors
  try {
    const blended = chroma.average(colors, 'rgb')
    return blended.alpha(0.7).hex() // Add some transparency
  } catch (error) {
    console.warn('Error blending colors:', error)
    return 'rgba(156, 163, 175, 0.6)' // Fallback to gray
  }
}

// Create a mapping of wrapped names to original names for easy lookup
const nameMapping = computed(() => {
  const mapping = new Map()
  
  if (showSuppliers.value) {
    // Map supplier names
    const supplierNames = new Set<string>()
    Object.values(props.data).forEach(result => {
      result.suppliers?.forEach(supplier => {
        if (supplier.quantity !== null && supplier.quantity !== undefined && supplier.quantity > 0) {
          supplierNames.add(supplier.supplier.name)
        }
      })
    })
    
    Array.from(supplierNames).forEach(supplierName => {
      const wrappedName = wrapText(supplierName, 25)
      mapping.set(wrappedName, supplierName)
    })
  } else {
    // Map species names
    Object.keys(props.data).forEach(originalName => {
      const wrappedName = wrapText(originalName, 25)
      mapping.set(wrappedName, originalName)
    })
  }
  
  return mapping
})

// Helper function to get CSS variable values
const getCSSVariableValue = (variable: string) => {
  if (import.meta.client) {
    const root = document.documentElement
    return getComputedStyle(root).getPropertyValue(variable).trim()
  }
  return ''
}

// Computed values for CSS variables that work with Chart.js
const themeColors = computed(() => {
  if (import.meta.client) {
    return {
      background: `hsl(${getCSSVariableValue('--background')})`,
      primary: `hsl(${getCSSVariableValue('--primary')})`,
      border: `hsl(${getCSSVariableValue('--border')})`,
      foreground: `hsl(${getCSSVariableValue('--foreground')})`
    }
  }
  
  // Fallback values for SSR
  return colorMode.value === 'dark' ? {
    background: '#0a0a0a',
    primary: '#fafafa', 
    border: '#27272a',
    foreground: '#fafafa'
  } : {
    background: '#ffffff',
    primary: '#0a0a0a',
    border: '#e4e4e7', 
    foreground: '#0a0a0a'
  }
})

// Check if we have valid data for chart rendering
const hasValidChartData = computed(() => {
  try {
    // Early return if no data or data is empty
    if (!props.data || typeof props.data !== 'object' || Object.keys(props.data).length === 0) {
      return false
    }
    
    const availableSpecies = Object.entries(props.data).filter(([_, result]) => 
      result && result.isAvailable && result.suppliers && result.suppliers.length > 0
    )
    
    // Need at least 2 items to create meaningful overlaps
    return availableSpecies.length >= 2
  } catch (error) {
    console.warn('Error checking chart data validity:', error)
    return false
  }
})

// Transform availability data to manual Euler diagram format (avoiding extractSets)
const chartData = computed(() => {
  // Return empty structure immediately if no valid data
  if (!hasValidChartData.value) {
    return { 
      labels: [],
      datasets: [{
        label: 'No Data',
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 2
      }]
    }
  }
  
  try {
    const availableSpecies = Object.entries(props.data).filter(([_, result]) => 
      result && result.isAvailable && result.suppliers && result.suppliers.length > 0
    )
    
    // Double-check we have valid species data
    if (availableSpecies.length < 2) {
      return { 
        labels: [],
        datasets: [{
          label: 'Insufficient Data',
          data: [],
          backgroundColor: [],
          borderColor: [],
          borderWidth: 2
        }]
      }
    }

    // Create manual data structure for Euler diagram
    const data: any[] = []
    const labels: string[] = []
    
    if (showSuppliers.value) {
      // SUPPLIERS MODE: Suppliers are circles, they overlap when stocking the same species
      
      // Collect all suppliers with stock and their species
      const supplierSpeciesMap = new Map<string, Set<string>>()
      const supplierCounts = new Map<string, number>()
      
      availableSpecies.forEach(([speciesName, result]) => {
        result.suppliers?.forEach(supplier => {
          if (supplier.quantity !== null && supplier.quantity !== undefined && supplier.quantity > 0) {
            const supplierName = supplier.supplier.name
            
            if (!supplierSpeciesMap.has(supplierName)) {
              supplierSpeciesMap.set(supplierName, new Set())
              supplierCounts.set(supplierName, 0)
            }
            
            supplierSpeciesMap.get(supplierName)!.add(speciesName)
            supplierCounts.set(supplierName, supplierCounts.get(supplierName)! + supplier.quantity)
          }
        })
      })
      
      const supplierEntries = Array.from(supplierSpeciesMap.entries())
      
      // Add individual supplier circles with comprehensive data structure
      supplierEntries.forEach(([supplierName, speciesSet], index) => {
        const shortName = wrapText(supplierName, 25)
        labels.push(shortName)
        
        data.push({
          sets: [shortName],
          value: Math.max(1, speciesSet.size), // Ensure minimum value of 1
          label: '', // Remove label to hide text on chart
          fullName: supplierName,
          speciesCount: speciesSet.size,
          totalQuantity: supplierCounts.get(supplierName) || 0,
          // Add properties that chartjs-chart-venn might expect
          size: speciesSet.size,
          weight: speciesSet.size,
          overlaps: [] // Initialize empty overlaps array
        })
      })
      
      // Add intersections (suppliers that stock the same species)
      for (let i = 0; i < supplierEntries.length; i++) {
        for (let j = i + 1; j < supplierEntries.length; j++) {
          const [supplier1Name, species1Set] = supplierEntries[i]
          const [supplier2Name, species2Set] = supplierEntries[j]
          
          // Find shared species
          const sharedSpecies = new Set(
            [...species1Set].filter(species => species2Set.has(species))
          )
          
          if (sharedSpecies.size > 0) {
            const shortName1 = wrapText(supplier1Name, 25)
            const shortName2 = wrapText(supplier2Name, 25)
            
            data.push({
              sets: [shortName1, shortName2],
              value: Math.max(1, sharedSpecies.size), // Ensure minimum value of 1
              sharedSpecies: Array.from(sharedSpecies),
              // Add properties that chartjs-chart-venn might expect
              size: sharedSpecies.size,
              weight: sharedSpecies.size,
              overlaps: [shortName1, shortName2] // Track which sets this overlaps
            })
          }
        }
      }
      
    } else {
      // SPECIES MODE: Species are circles, they overlap when sharing suppliers (original logic)
      
      // Create sets for each species with their suppliers
      const speciesSets = availableSpecies.map(([speciesName, result], index) => {
        // Get suppliers with stock (qty > 0 and not null/undefined)
        const suppliersWithStock = result.suppliers?.filter(supplier => 
          supplier.quantity !== null && 
          supplier.quantity !== undefined && 
          supplier.quantity > 0
        ) || []
        
        // Create unique identifier for each supplier
        const supplierIds = new Set(suppliersWithStock.map(supplier => 
          `${supplier.supplier.name}_${supplier.size || 'unknown'}`
        ))
        
        // Create shorter label without supplier count
        const shortName = wrapText(speciesName, 25)
        labels.push(shortName)
        
        return {
          label: shortName,
          suppliers: supplierIds,
          fullName: speciesName,
          supplierCount: suppliersWithStock.length,
          index
        }
      }).filter(set => set.suppliers.size > 0)
      
      // Add individual species circles with comprehensive data structure
      speciesSets.forEach((species, index) => {
        data.push({
          sets: [species.label],
          value: Math.max(1, species.supplierCount), // Ensure minimum value of 1
          label: '', // Remove label to hide text on chart
          fullName: species.fullName,
          supplierCount: species.supplierCount,
          // Add properties that chartjs-chart-venn might expect
          size: species.supplierCount,
          weight: species.supplierCount,
          overlaps: [] // Initialize empty overlaps array
        })
      })
      
      // Add intersections (species that share suppliers)
      for (let i = 0; i < speciesSets.length; i++) {
        for (let j = i + 1; j < speciesSets.length; j++) {
          const species1 = speciesSets[i]
          const species2 = speciesSets[j]
          
          // Find shared suppliers
          const sharedSuppliers = new Set(
            [...species1.suppliers].filter(supplier => species2.suppliers.has(supplier))
          )
          
          if (sharedSuppliers.size > 0) {
            data.push({
              sets: [species1.label, species2.label],
              value: Math.max(1, sharedSuppliers.size), // Ensure minimum value of 1
              sharedSuppliers: Array.from(sharedSuppliers),
              // Add properties that chartjs-chart-venn might expect
              size: sharedSuppliers.size,
              weight: sharedSuppliers.size,
              overlaps: [species1.label, species2.label] // Track which sets this overlaps
            })
          }
        }
      }
    }
    
    // Validate all data points to ensure they have required properties
    const validatedData = data.map((item, index) => ({
      ...item,
      // Ensure all required properties exist
      sets: Array.isArray(item.sets) ? item.sets : [item.label || `item_${index}`],
      value: typeof item.value === 'number' && item.value > 0 ? item.value : 1,
      size: typeof item.size === 'number' && item.size > 0 ? item.size : item.value || 1,
      weight: typeof item.weight === 'number' && item.weight > 0 ? item.weight : item.value || 1,
      overlaps: Array.isArray(item.overlaps) ? item.overlaps : [],
      // Add index for library compatibility
      index: index,
      // Add id for library compatibility  
      id: item.label || `item_${index}`
    }))
    
    // Create background colors for each data point
    const backgroundColors = validatedData.map((item, index) => {
      if (item.sets && item.sets.length === 1) {
        // Single set - use item color
        const itemEntries = showSuppliers.value ? 
          Array.from(new Set(Object.values(props.data).flatMap(result => 
            result.suppliers?.filter(s => s.quantity && s.quantity > 0).map(s => s.supplier.name) || []
          ))) :
          Object.keys(props.data)
        
        const setIndex = itemEntries.findIndex(name => {
          const wrappedName = wrapText(name, 25)
          return item.sets.includes(wrappedName)
        })
        return getItemColor(setIndex >= 0 ? setIndex : index)
      } else if (item.sets && item.sets.length > 1) {
        // Intersection - blend colors of overlapping items
        const itemEntries = showSuppliers.value ? 
          Array.from(new Set(Object.values(props.data).flatMap(result => 
            result.suppliers?.filter(s => s.quantity && s.quantity > 0).map(s => s.supplier.name) || []
          ))) :
          Object.keys(props.data)
        
        const setIndices = item.sets
          .map((setLabel: string) => {
            const originalName = nameMapping.value.get(setLabel) || setLabel
            return itemEntries.indexOf(originalName)
          })
          .filter((index: number) => index >= 0)
        
        return blendColors(setIndices)
      }
      return getItemColor(index)
    })
    
    // Create border colors
    const borderColors = validatedData.map(() => 'rgba(255, 255, 255, 0.8)')
    
    return {
      labels,
      datasets: [{
        label: showSuppliers.value ? 'Supplier Relationships' : 'Species Relationships',
        data: validatedData,
        backgroundColor: backgroundColors,
        borderColor: borderColors,
        borderWidth: 2
      }]
    }
  } catch (error) {
    console.error('Error creating Euler diagram:', error)
    // Clean up on error
    if (chartRef.value) {
      chartRef.value.destroy()
      chartRef.value = null
    }
    return { 
      labels: [],
      datasets: [{
        label: 'Error',
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 2
      }]
    }
  }
})

// Chart configuration
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 0,
      right: 0,
      bottom: 50,
      left: 0
    }
  },
  // Add interaction configuration to prevent errors
  interaction: {
    intersect: false,
    mode: 'nearest'
  },
  // Add onClick handler to catch and prevent errors
  onClick: (event: any, elements: any[], chart: any) => {
    
  },
  plugins: {
    legend: {
      display: true,
      position: 'bottom' as const,
      align: 'start' as const,
      labels: {
        color: themeColors.value.primary,
        font: {
          size: 15
        },
        padding: 12,
        usePointStyle: true,
        boxWidth: 10,
        boxHeight: 10,
        generateLabels: (chart: any) => {
          // Generate custom legend labels for individual items only
          const datasets = chart.data.datasets[0]?.data || []
          const itemLabels: any[] = []
          
          // Only show individual items (sets with length 1), not intersections
          datasets.forEach((item: any, index: number) => {
            if (item.sets && item.sets.length === 1) {
              const itemLabel = item.sets[0]
              // Use name mapping for safe lookup
              const originalName = nameMapping.value.get(itemLabel) || itemLabel
              
              const itemEntries = showSuppliers.value ? 
                Array.from(new Set(Object.values(props.data).flatMap(result => 
                  result.suppliers?.filter(s => s.quantity && s.quantity > 0).map(s => s.supplier.name) || []
                ))) :
                Object.keys(props.data)
              
              const setIndex = itemEntries.indexOf(originalName)
              
              // Use the original name for legend (unwrapped)
              itemLabels.push({
                text: originalName,
                fillStyle: getItemColor(setIndex >= 0 ? setIndex : index),
                strokeStyle: 'rgba(255, 255, 255, 0.8)',
                lineWidth: 1,
                hidden: false
              })
            }
          })
          
          return itemLabels
        }
      },
      onClick: () => {}, // Disable legend click
      maxHeight: 120
    },
    tooltip: {
      backgroundColor: themeColors.value.background,
      titleColor: themeColors.value.primary,
      bodyColor: themeColors.value.primary,
      borderColor: themeColors.value.border,
      borderWidth: 1,
      cornerRadius: 8,
      padding: 12,
      displayColors: true,
      titleFont: {
        size: 12,
        weight: 'bold'
      },
      bodyFont: {
        size: 11
      },
      // Add filter to prevent errors on invalid data
      filter: (tooltipItem: any) => {
        try {
          return tooltipItem && tooltipItem.raw && tooltipItem.raw.sets
        } catch (error) {
          console.warn('Tooltip filter error:', error)
          return false
        }
      },
      callbacks: {
        title: (context: any[]) => {
          try {
            const dataPoint = context[0]?.raw
            if (dataPoint?.sets && dataPoint.sets.length === 1) {
              // Show full item name in tooltip
              const labelText = dataPoint.sets[0]
              // Use name mapping for safe lookup
              const originalName = nameMapping.value.get(labelText) || labelText
              return originalName
            } else if (dataPoint?.sets && dataPoint.sets.length > 1) {
              return showSuppliers.value ? 'Shared Species' : 'Shared Suppliers'
            }
            return showSuppliers.value ? 'Supplier Data' : 'Species Data'
          } catch (error) {
            console.warn('Tooltip title error:', error)
            return 'Data'
          }
        },
        label: (context: any) => {
          try {
            const dataPoint = context.raw
            if (dataPoint?.sets && Array.isArray(dataPoint.sets)) {
              if (dataPoint.sets.length === 1) {
                if (showSuppliers.value) {
                  return `Species stocked: ${dataPoint.speciesCount || dataPoint.value}`
                } else {
                  // Find the supplier count for this species
                  const labelText = dataPoint.sets[0]
                  const speciesName = nameMapping.value.get(labelText) || labelText
                  
                  const supplierCount = props.data[speciesName]?.suppliers?.filter(supplier => 
                    supplier.quantity !== null && 
                    supplier.quantity !== undefined && 
                    supplier.quantity > 0
                  ).length || 0
                  
                  return `Total suppliers with stock: ${supplierCount}`
                }
              } else {
                // For intersections
                if (showSuppliers.value) {
                  const supplierNames = dataPoint.sets.map((setLabel: string) => {
                    return nameMapping.value.get(setLabel) || setLabel
                  }).join(' & ')
                  return `Both suppliers stock: ${dataPoint.sharedSpecies?.join(', ') || 'shared species'}`
                } else {
                  const speciesNames = dataPoint.sets.map((setLabel: string) => {
                    return nameMapping.value.get(setLabel) || setLabel
                  }).join(' & ')
                  return `Shared by: ${speciesNames}`
                }
              }
            }
            return `Value: ${dataPoint?.value || 0}`
          } catch (error) {
            console.warn('Tooltip label error:', error)
            return 'Data'
          }
        },
        afterLabel: (context: any) => {
          try {
            const dataPoint = context.raw
            if (dataPoint?.sets && dataPoint.sets.length > 1) {
              if (showSuppliers.value) {
                return `Common species: ${dataPoint.value || 0}`
              } else {
                return `Common suppliers: ${dataPoint.value || 0}`
              }
            }
            return ''
          } catch (error) {
            console.warn('Tooltip afterLabel error:', error)
            return ''
          }
        }
      }
    }
  },
  scales: {
    x: {
      display: false,
      min: -1.4,
      max: 1.4,
      ticks: {
        display: true // Keep set value labels within intersections
      }
    },
    y: {
      display: false,
      min: -1.4,
      max: 1.4,
      ticks: {
        display: false // Disable set label labels next to set circles
      }
    }
  },
  animation: {
    duration: 800,
    easing: 'easeOutQuart'
  }
}))

const isChartReady = ref(false)

// Create chart instance
const createChart = () => {
  if (!canvasRef.value || !hasValidChartData.value) {
    console.log('Cannot create chart: missing canvas or invalid data')
    return
  }
  
  // Destroy existing chart first
  if (chartRef.value) {
    chartRef.value.destroy()
    chartRef.value = null
  }
  
  try {
    // Ensure canvas has proper context before creating chart
    const context = canvasRef.value.getContext('2d')
    if (!context) {
      console.error('Failed to get canvas 2D context')
      return
    }
    
    // Validate chart data before creating
    const data = chartData.value
    if (!data || !data.datasets || data.datasets.length === 0) {
      console.log('No datasets available for chart')
      return
    }
    
    // Ensure we have proper data structure to avoid the undefined length error
    const dataset = data.datasets[0]
    if (!dataset || !dataset.data || !Array.isArray(dataset.data)) {
      console.log('Invalid dataset structure')
      return
    }
    
    // Check if we have any data points
    if (dataset.data.length === 0) {
      console.log('No data points in dataset')
      return
    }
    
    // Validate that all data points have proper structure
    const validData = dataset.data.every(item => 
      item && 
      typeof item === 'object' && 
      item.sets && 
      Array.isArray(item.sets) && 
      item.sets.length > 0 &&
      typeof item.value === 'number' &&
      item.value > 0
    )
    
    if (!validData) {
      console.error('Invalid data points structure:', dataset.data)
      return
    }
    
    // Log the data we're about to send to the chart
    console.log('Creating Euler chart with data:', {
      labels: data.labels,
      dataPoints: dataset.data.length,
      sampleData: dataset.data.slice(0, 3)
    })
    
    // Create new chart with additional error handling
    chartRef.value = new ChartJS(canvasRef.value, {
      type: 'euler',
      data: data,
      options: {
        ...chartOptions.value,
        // Add animation false to prevent timing issues
        animation: false,
        // Add responsive handling
        responsive: true,
        maintainAspectRatio: false,
        // Add error handling for onError
        onError: (error: any) => {
          console.error('Chart.js error:', error)
          // Clean up on chart error
          if (chartRef.value) {
            chartRef.value.destroy()
            chartRef.value = null
          }
        }
      }
    })
    
    console.log('Chart created successfully')
  } catch (error) {
    console.error('Error creating Euler diagram:', error)
    // Clean up on error
    if (chartRef.value) {
      try {
        chartRef.value.destroy()
      } catch (destroyError) {
        console.error('Error destroying chart during cleanup:', destroyError)
      } finally {
        chartRef.value = null
      }
    }
  }
}

// Watch for data changes
watch(
  () => props.data,
  (newData) => {
    // Guard against undefined or empty data
    if (!newData || typeof newData !== 'object' || Object.keys(newData).length === 0) {
      console.log('Data not ready for chart, skipping update')
      return
    }
    
    if (hasValidChartData.value) {
      nextTick(() => {
        // Add small delay to ensure DOM is fully ready
        setTimeout(() => {
          createChart()
        }, 100)
      })
    }
  },
  { deep: true, immediate: false }
)

// Watch for toggle changes
watch(
  () => showSuppliers.value,
  () => {
    // Only create chart if we have valid data
    if (hasValidChartData.value && props.data && Object.keys(props.data).length > 0) {
      nextTick(() => {
        setTimeout(() => {
          createChart()
        }, 100)
      })
    }
  }
)

// Watch for color mode changes
watch(
  () => colorMode.value,
  () => {
    if (chartRef.value) {
      // Recreate chart with new theme
      createChart()
    }
  }
)

// Set chart ready state after mount
onMounted(() => {
  console.log('AvailabilityEulerDiagram mounted')
  console.log('Props data:', props.data)
  console.log('hasValidChartData:', hasValidChartData.value)
  
  nextTick(() => {
    isChartReady.value = true
    
    // Only create chart if we have valid data and it's not empty
    if (hasValidChartData.value && props.data && Object.keys(props.data).length > 0) {
      // Add delay to ensure canvas is fully mounted and ready
      setTimeout(() => {
        createChart()
      }, 200)
    } else {
      console.log('Skipping chart creation - no valid data available on mount')
    }
  })
})

// Cleanup on unmount
onUnmounted(() => {
  console.log('Component unmounting, cleaning up chart')
  if (chartRef.value) {
    try {
      chartRef.value.destroy()
    } catch (error) {
      console.error('Error destroying chart:', error)
    } finally {
      chartRef.value = null
    }
  }
})
</script>

<template>
  <div class="w-full">
    <!-- Toggle Switch -->
    <div class="flex items-center space-x-2 mb-4 px-4">
      <Label for="view-mode" class="text-sm text-muted-foreground">
        {{ showSuppliers ? 'Suppliers' : 'Species' }}
      </Label>
      <Switch 
        id="view-mode"
        v-model:checked="showSuppliers" 
        class="data-[state=checked]:bg-primary"
      />
      <span class="text-xs text-muted-foreground">
        {{ showSuppliers ? 'View by suppliers' : 'View by species' }}
      </span>
    </div>

    <div v-if="!hasValidChartData" class="flex items-center justify-center h-64 text-muted-foreground">
      <div class="text-center">
        <p>This chart works best with more than one species in your schedule.</p>
      </div>
    </div>
    
    <div v-else-if="!isChartReady" class="flex items-center justify-center h-64 text-muted-foreground">
      <div class="text-center">
        <Icon name="lucide:loader-2" class="w-8 h-8 mx-auto mb-2 animate-spin" />
        <p>Loading chart...</p>
      </div>
    </div>
    
    <div v-else class="relative">
      <div class="h-[500px] w-full p-4">
        <canvas ref="canvasRef" class="w-full h-full" />
      </div>
      
      <!-- Updated info text based on mode -->
      <div class="mt-2 text-xs text-muted-foreground px-4">
        <p>
          <Icon name="lucide:info" class="w-3 h-3 inline mr-1" />
          <span v-if="showSuppliers">
            Each circle represents a supplier. Overlapping areas show suppliers that stock the same species.
          </span>
          <span v-else>
            Each circle represents a plant species. Overlapping areas show species that share suppliers.
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
canvas {
  max-width: 100%;
  height: auto;
}
</style> 